import React from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Card, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight03Icon } from "@hugeicons-pro/core-stroke-standard";
import { titleCase } from "title-case";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import type {
  BankAccount,
  UseGetBankAccountsResult,
} from "hooks/useGetBankAccounts";
import useCashFlowSummary from "hooks/useCashFlowSummary";
import { Center, Stack } from "components/layout/reusableLayouts";
import BalanceAmount from "components/common/BalanceAmount";
import TrendChart from "components/common/TrendChart";
import { formatCashFull } from "utils/formatCash";

interface CashFlowPanelProps {
  bankAccounts: UseGetBankAccountsResult;
  /** The account the page is scoped to, or null for every account. */
  accountId: string | null;
  timeFrame: TimeFrameEnum;
}

const PANEL_MIN_HEIGHT = 360;

const CashFlowPanel: React.FC<CashFlowPanelProps> = ({
  bankAccounts,
  accountId,
  timeFrame,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    allAccounts,
    isLoading: loadingAccounts,
    loadingBalances,
    combinedBalances,
  } = bankAccounts;
  const loading = loadingAccounts || loadingBalances;

  // TODO: multiple financial accounts are not supported; the first is the main one.
  const financialAccount = allAccounts.find(
    (account) => account.accountType === AccountType.FINANCIAL_ACCOUNT,
  );

  // An id that is no longer listed falls back to the main account rather than to "all".
  let selectedAccount: BankAccount | undefined;
  let chartAccountId = accountId ?? undefined;
  if (accountId) {
    selectedAccount =
      allAccounts.find((account) => account.id === accountId) ??
      financialAccount;
    if (selectedAccount) chartAccountId = selectedAccount.id;
  }

  const isExternalCard =
    selectedAccount?.accountType === AccountType.PLAID_EXTERNAL_CARD_ACCOUNT;
  const showChart = !accountId || (!!selectedAccount && !isExternalCard);

  // A curve nobody sees is not fetched: a BYOC account, or an account not listed yet.
  const cashFlow = useCashFlowSummary(
    timeFrame,
    chartAccountId,
    !showChart,
    true,
  );
  // A credit line belongs to one charge program: the one selected, or the company's only program. A combined total
  // across programs mixes cash with credit, so it is not labelled as either.
  const programs = allAccounts.filter(
    (account) => account.accountType === AccountType.FINANCIAL_ACCOUNT,
  );
  const creditAccount =
    selectedAccount ?? (programs.length === 1 ? programs[0] : undefined);
  const isChargeAccount =
    creditAccount?.applicationType === ApplicationType.CHARGE;

  // A nickname is runtime data, so it never goes through t().
  const accountName = (account: BankAccount) =>
    titleCase(
      (
        account.nickname ||
        account.bankName ||
        t("your main account")
      ).toLowerCase(),
    );

  const title = selectedAccount ? accountName(selectedAccount) : t("Cash Flow");

  const combinedIsZero =
    combinedBalances === "0" || combinedBalances === "$0.00";
  const balance = selectedAccount
    ? selectedAccount.availableAmount
    : loading && combinedIsZero
      ? undefined
      : combinedBalances;

  const today = new Date().toLocaleDateString(
    i18n.language === "es" ? "es-ES" : "en-US",
    { month: "long", day: "2-digit", year: "numeric" },
  );

  // A list still loading has no balance yet; one that loaded without it has none to show.
  const valueLoading = !isExternalCard && !balance && loading;
  const creditLabel =
    isChargeAccount && !isExternalCard && creditAccount?.creditLimit
      ? t("Available Credit of {{limit}} limit", {
          limit: creditAccount.creditLimit,
        })
      : null;

  return (
    <Card
      variant="outlined"
      title={title}
      value={
        isExternalCard ? undefined : balance ? (
          <BalanceAmount
            value={balance}
            variant="inherit"
            centsSize="var(--type-size-lg)"
          />
        ) : valueLoading ? (
          ""
        ) : (
          "-"
        )
      }
      caption={
        <Stack gap="var(--space-1)">
          {creditLabel && <span>{creditLabel}</span>}
          <span>{today}</span>
        </Stack>
      }
      loading={valueLoading}
      action={{
        type: "content",
        content: (
          <IconButton
            variant="ghost"
            aria-label={t("View transactions")}
            onClick={() =>
              navigate("/admin/transactions", {
                state: selectedAccount
                  ? { accountId: selectedAccount.id }
                  : undefined,
              })
            }
          >
            <HugeiconsIcon icon={ArrowUpRight03Icon} size={20} />
          </IconButton>
        ),
      }}
      sx={{ minHeight: PANEL_MIN_HEIGHT }}
    >
      {isExternalCard && (
        <Center sx={{ flex: 1, minWidth: 0 }}>
          <Stack gap="var(--space-4)">
            <Divider />
            <Typography
              variant="label"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              <Trans i18nKey="byoc_account_info">
                This is a BYOC (Bring Your Own Card) account. Balance is managed
                externally and not available for card spend or reimbursements
                through Outpave.
              </Trans>
            </Typography>
            <Divider />
          </Stack>
        </Center>
      )}

      {showChart && (
        <TrendChart
          series={cashFlow.series.map((point) => ({
            date: point.date,
            value: point.balance,
          }))}
          formatValue={(value) => formatCashFull(value, cashFlow.currency)}
          loading={cashFlow.loading}
        />
      )}
    </Card>
  );
};

export default CashFlowPanel;
